/**
 * @render react
 * @name Accordion
 * @example
 * <Accordion allowMultipleOpen items={[{ id: 'accordion-1', title: 'Collapsible 1', content: 'Content 1', isOpen: true }, { id: 'accordion-2', title: 'Collapsible 2', content: 'Content 2' }, { id: 'accordion-3', title: 'Collapsible 3', content: 'Content 3' } ]} />
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Collapsible } from 'Components/molecules/Collapsible';

import './_style.scss';

/**
 * Accordion component
 * @param {Array} items - accordion items - array of objects (title, content)
 * @param {boolean} exclusive - Collapse/expand 1 item at a time.
 * @param {boolean} firstIsOpen - Whether to have first option open on load - default is true.
 * @param {string} iconClose - close icon to show (default -)
 * @param {string} iconOpen - open icon to show (default +)
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 */
class Accordion extends Component {
  /**
   * Constructor
   */
  constructor(props) {
    super(props);

    const collapsibles = [];
    const { items, firstIsOpen } = this.props;

    // Build a list of collapsibles
    items.forEach((item, index) => {
      collapsibles[index] = {
        ...item,
        isOpen: (firstIsOpen && index === 0) || false,
      };
    });

    this.state = {
      collapsibles,
    };

    this.toggleCollapsible = this.toggleCollapsible.bind(this);
  }

  /**
   * Toggles a collapsible
   */
  toggleCollapsible(key) {
    const { exclusive } = this.props;
    const { collapsibles } = this.state;

    // if we want one collapsible to be opened at a given time
    if (exclusive) {
      // we close all
      collapsibles.forEach((item) => {
        item.isOpen = false; // eslint-disable-line
      });
    }

    if (collapsibles[key]) {
      collapsibles[key].isOpen = !collapsibles[key].isOpen;
    }

    this.setState({
      collapsibles,
    });
  }

  /**
   * Renders component
   */
  render() {
    const {
      iconOpen,
      iconClose,
      loadStyles,
      extraClassNames,
      context: { cssPrefix },
    } = this.props;
    const {
      collapsibles,
    } = this.state;

    return (
      <div
        className={classNames({
          [`${cssPrefix}__accordion`]: loadStyles,
          ...extraClassNames,
        })}
      >
        {collapsibles.map((item, index) => (
          <Collapsible
            key={index}
            {...item}
            extraClassNames={{ [`${cssPrefix}__accordion--item`]: true }}
            isOpen={item.isOpen}
            onToggle={() => { this.toggleCollapsible(index); }}
            iconOpen={iconOpen}
            iconClose={iconClose}
          />
        ))}
      </div>
    );
  }
}

/**
 * Valid Props
 */
Accordion.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape()),
  iconOpen: PropTypes.string,
  iconClose: PropTypes.string,
  exclusive: PropTypes.bool,
  firstIsOpen: PropTypes.bool,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
};

/**
 * Default Props
 */
Accordion.defaultProps = {
  iconOpen: null,
  iconClose: null,
  items: [],
  exclusive: true,
  firstIsOpen: true,
  loadStyles: true,
  extraClassNames: {},
  context: {},
};

export default withPolaris(Accordion);
